import React, { useEffect, useMemo, useState } from "react"
import { useUserPosts } from "../../../hooks/useUserPosts"
import Heading from "../../shared/Heading"
import { RequestError } from "../../shared/RequestError"
import { Spinner } from "../../shared/Spinner"
import CalendarEvent from "./CalendarEvent"
import type { User } from "../../../types/bands"

interface Post {
  id: number
  [key: string]: unknown
}

interface UserCalendarProps {
  user: User
}

const UserCalendar: React.FC<UserCalendarProps> = ({ user }) => {
  const PER_PAGE = 20
  const [page, setPage] = useState(1)
  const [_calendarEvents, setCalendarEvents] = useState<Post[]>([])
  const calendarEvents = useMemo(() => _calendarEvents, [_calendarEvents])

  const futureQuery = useUserPosts(user.id, "future_concerts", page, PER_PAGE)
  const pastQuery = useUserPosts(user.id, "past_concerts", 1, 5)

  useEffect(() => {
    if (!futureQuery.isLoading && !futureQuery.isError && futureQuery.data?.posts) {
      if (page === 1) {
        setCalendarEvents(futureQuery.data.posts)
      } else {
        setCalendarEvents((prev) => [...prev, ...futureQuery.data.posts])
      }
    }
  }, [futureQuery.data])

  const pastEvents: Post[] = (!pastQuery.isLoading && !pastQuery.isError)
    ? (pastQuery.data?.posts ?? [])
    : []

  return (
    <>
      <Heading level={2} type="sectionHeader">
        Your Calendar Events
      </Heading>
      <div className="flex flex-col">
        {futureQuery.isLoading ? (
          <Spinner />
        ) : futureQuery.error ? (
          <RequestError error={futureQuery.error} />
        ) : (
          <>
            <ul>
              {calendarEvents.length === 0 && pastEvents.length === 0 ? (
                <li className="italic text-gray-600 text-sm">
                  There aren't any upcoming shows that you have RSVP'd for. Try
                  clicking the "Shows" filter on your Band Feed and look for
                  some.
                </li>
              ) : (
                <>
                  {calendarEvents.map((calendarEvent) => (
                    <CalendarEvent
                      key={`future-event-${calendarEvent.id}`}
                      calendarEvent={calendarEvent}
                    />
                  ))}
                  {pastEvents.map((calendarEvent) => (
                    <CalendarEvent
                      key={`past-event-${calendarEvent.id}`}
                      calendarEvent={calendarEvent}
                      isPast={true}
                    />
                  ))}
                </>
              )}
            </ul>
            {futureQuery.data?.has_next_page && (
              <button
                className="text-sm text-blue-600 hover:text-blue-800 text-left focus:outline-none mt-2"
                onClick={() => setPage((p) => p + 1)}
                disabled={futureQuery.isFetching}
              >
                {futureQuery.isFetching ? "Loading..." : "Show more"}
              </button>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default UserCalendar
